import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getCrmMembers, getLossReasons, getStages, moveDeal } from '../../db/api.js'

function MoveConfirmation({ deal, current, target, onClose, onMoved }) {
  const dialog = useRef(null)
  const lock = useRef(false)
  const [stages, setStages] = useState([])
  const [stageId, setStageId] = useState('')
  const [members, setMembers] = useState([])
  const [ownerId, setOwnerId] = useState(deal.owner_id || '')
  const [reasons, setReasons] = useState([])
  const [reasonId, setReasonId] = useState('')
  const [justification, setJustification] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const destination = stages.find(stage => stage.id === stageId)
  useEffect(() => { dialog.current?.showModal() }, [])
  useEffect(() => {
    let alive = true
    Promise.all([getStages(target.id), getCrmMembers()]).then(([rows, users]) => {
      if (!alive) return
      setStages(rows); setMembers(users)
      setStageId((rows.find(stage => !stage.is_ganho && !stage.is_perdido) || rows[0])?.id || '')
    })
      .catch(err => { if (alive) setError(err.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [target.id])
  useEffect(() => {
    if (!destination?.is_perdido) return
    let alive = true
    getLossReasons().then(rows => { if (alive) setReasons(rows) }).catch(err => { if (alive) setError(err.message) })
    return () => { alive = false }
  }, [destination?.is_perdido])
  const close = () => { if (!lock.current) onClose() }
  const confirm = async () => {
    if (lock.current || !destination) return
    lock.current = true; setSaving(true); setError('')
    try {
      const updated = await moveDeal(deal.id, {
        toStageId: destination.id,
        ...(ownerId !== (deal.owner_id || '') ? { ownerId: ownerId || null } : {}),
        lossReasonId: destination.is_perdido ? reasonId || null : null,
        justificativa: destination.is_perdido ? justification.trim() || null : null,
      })
      onMoved?.(updated, stages)
      onClose()
    } catch (err) { setError(err.message) }
    finally { lock.current = false; setSaving(false) }
  }
  return createPortal(<dialog ref={dialog} className="dp-discount-dialog dp-pipeline-dialog" aria-labelledby="dp-pipeline-confirm-title" onCancel={(event) => { event.preventDefault(); close() }} onKeyDown={(event) => { if (event.key === 'Escape') event.stopPropagation() }} onClick={(event) => { if (event.target === event.currentTarget) close() }}>
    <h2 id="dp-pipeline-confirm-title">Mover negócio de pipeline?</h2>
      <p>Tem certeza que deseja mover este negócio de <strong>{current}</strong> para <strong>{target.nome}</strong>?</p>
      <p>O mesmo negócio será movido, mantendo seu histórico, contatos, empresa e itens.</p>
      <div aria-busy={loading}>
        <div className="fc-field"><label className="fc-label" htmlFor="pipeline-move-stage">Etapa de destino</label><select id="pipeline-move-stage" className="fc-select" value={stageId} disabled={saving || loading} onChange={event => setStageId(event.target.value)}>
          {!stages.length && <option value="">{loading ? 'Carregando etapas…' : 'Nenhuma etapa disponível'}</option>}
          {stages.map(stage => <option key={stage.id} value={stage.id}>{stage.nome}</option>)}
        </select></div>
        <div className="fc-field"><label className="fc-label" htmlFor="pipeline-move-owner">Responsável</label><select id="pipeline-move-owner" className="fc-select" value={ownerId} disabled={saving || loading} onChange={event => setOwnerId(event.target.value)}>
          <option value="">{loading ? 'Carregando responsáveis…' : 'Sem responsável'}</option>
          {deal.owner_id && !members.some(member => member.id === deal.owner_id) && <option value={deal.owner_id}>{loading ? 'Carregando responsáveis…' : 'Manter responsável atual'}</option>}
          {members.map(member => <option key={member.id} value={member.id}>{member.nome || 'Usuário sem nome'}</option>)}
        </select></div>
        {destination && <p>{destination.is_ganho ? 'O negócio será marcado como ganho.' : destination.is_perdido ? 'O negócio será marcado como perdido.' : deal.status !== 'aberto' ? 'O negócio será reaberto nessa etapa.' : ''}</p>}
        {destination?.is_perdido && <>
          <div className="fc-field"><label className="fc-label" htmlFor="pipeline-move-reason">Motivo da perda</label><select id="pipeline-move-reason" className="fc-select" disabled={saving} value={reasonId} onChange={event => setReasonId(event.target.value)}><option value="">Selecione</option>{reasons.map(reason => <option key={reason.id} value={reason.id}>{reason.nome}</option>)}</select></div>
          <div className="fc-field"><label className="fc-label" htmlFor="pipeline-move-justification">Justificativa (opcional)</label><textarea id="pipeline-move-justification" className="fc-input" disabled={saving} value={justification} onChange={event => setJustification(event.target.value)} /></div>
        </>}
      </div>
      {error && <p role="alert" className="fc-error">{error}</p>}
    <div className="dp-discount-actions"><button type="button" disabled={saving} onClick={close}>Cancelar</button><button type="button" className="dp-pipeline-confirm" disabled={saving || loading || !destination} onClick={confirm}>{saving ? 'Movendo…' : 'Confirmar movimentação'}</button></div>
  </dialog>, document.body)
}

export default function DealPipelinePicker({ deal, pipelines, currentName, onMoved }) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState(null)
  const root = useRef(null)
  const trigger = useRef(null)
  const options = pipelines
  useEffect(() => {
    if (!open) return
    const outside = (event) => { if (!root.current?.contains(event.target)) setOpen(false) }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open])
  const close = () => { setTarget(null); trigger.current?.focus() }
  return <div className="cv-data-row dp-pipeline-inline dp-pipeline-picker" ref={root} onKeyDown={(event) => { if (open && event.key === 'Escape') { event.stopPropagation(); setOpen(false); trigger.current?.focus() } }}>
    <span className="cv-data-label">Pipeline</span>
    <button ref={trigger} type="button" className="cv-data-value dp-pipeline-trigger" aria-expanded={open} aria-label={`Escolher pipeline: ${currentName || 'Não informado'}`} onClick={() => setOpen(!open)}>{currentName || '—'}</button>
    {open && <div className="dp-pipeline-options" role="group" aria-label="Funis disponíveis">{options.map((pipeline) => <button type="button" key={pipeline.id} disabled={pipeline.id === deal.pipeline_id} onClick={() => { setOpen(false); setTarget(pipeline) }}>{pipeline.nome}{pipeline.id === deal.pipeline_id && <small>Atual</small>}</button>)}{!options.length && <span>Nenhum funil disponível.</span>}</div>}
    {target && <MoveConfirmation deal={deal} current={currentName || 'pipeline atual'} target={target} onClose={close} onMoved={onMoved} />}
  </div>
}
